import { lazy } from "react";
import type { RouteConfig } from "../config";
import { ROUTES } from "../paths";

const ClientsPage = lazy(() =>
  import("@/pages/clients").then((m) => ({ default: m.ClientsPage }))
);
const ClientDetailPage = lazy(() =>
  import("@/pages/clients").then((m) => ({ default: m.ClientDetailPage }))
);
const ClientOrdersPage = lazy(() =>
  import("@/pages/clients/orders/orders-list-page").then((m) => ({
    default: m.ClientOrdersPage,
  }))
);
const OrderDetailPage = lazy(() =>
  import("@/pages/clients/orders/order-detail-page").then((m) => ({
    default: m.OrderDetailPage,
  }))
);
const CreateOrderPage = lazy(() =>
  import("@/pages/clients/orders/order-create-page").then((m) => ({
    default: m.CreateOrderPage,
  }))
);
const ImportPdfOrderPage = lazy(() =>
  import("@/pages/clients/orders/order-import-pdf-page").then((m) => ({
    default: m.ImportPdfOrderPage,
  }))
);
const ClientInvoicesPage = lazy(() =>
  import("@/pages/clients/invoices/invoices-page").then((m) => ({
    default: m.ClientInvoicesPage,
  }))
);
const ClientArticlesPage = lazy(() =>
  import("@/pages/clients/articles/articles-list-page").then((m) => ({
    default: m.ClientArticlesPage,
  }))
);
const ArticleViewPage = lazy(() =>
  import("@/pages/articles/article-detail-page").then((m) => ({
    default: m.ArticleViewPage,
  }))
);
const ClientFactoryOrdersPage = lazy(() =>
  import("@/pages/clients/factory-orders/factory-orders-list-page").then(
    (m) => ({ default: m.ClientFactoryOrdersPage })
  )
);
const FactoryOrderDetailPage = lazy(() =>
  import("@/pages/clients/factory-orders/factory-order-detail-page").then(
    (m) => ({ default: m.FactoryOrderDetailPage })
  )
);
const ClientExpeditionPage = lazy(() =>
  import("@/pages/clients/expeditions/expedition-page").then((m) => ({
    default: m.ClientExpeditionPage,
  }))
);
const ShipmentDetailPage = lazy(() =>
  import("@/pages/clients/expeditions/shipment-detail-page").then((m) => ({
    default: m.ShipmentDetailPage,
  }))
);
const ContactsPage = lazy(() =>
  import("@/pages/contacts/contacts-page").then((m) => ({
    default: m.ContactsPage,
  }))
);

const OfficeStockPage = lazy(() =>
  import("@/pages/reception/office-stock-page").then((m) => ({
    default: m.OfficeStockPage,
  }))
);
const ReceptionCallsPage = lazy(() =>
  import("@/pages/reception/calls-page").then((m) => ({
    default: m.ReceptionCallsPage,
  }))
);
const ReceptionMailPage = lazy(() =>
  import("@/pages/reception/mail-page").then((m) => ({
    default: m.ReceptionMailPage,
  }))
);
const ReceptionPrintersPage = lazy(() =>
  import("@/pages/reception/printers-page").then((m) => ({
    default: m.ReceptionPrintersPage,
  }))
);
const ReceptionShipmentsPage = lazy(() =>
  import("@/pages/reception/shipments-page").then((m) => ({
    default: m.ReceptionShipmentsPage,
  }))
);
const ReceptionInkCartridgesPage = lazy(() =>
  import("@/pages/reception/ink-cartridges-page").then((m) => ({
    default: m.ReceptionInkCartridgesPage,
  }))
);
const ShipmentSimulationPage = lazy(() =>
  import("@/pages/reception/shipment-simulation-page").then((m) => ({
    default: m.ShipmentSimulationPage,
  }))
);
const OfficeSupplyRequestsPage = lazy(() =>
  import("@/pages/reception/office-supply-requests-page").then((m) => ({
    default: m.OfficeSupplyRequestsPage,
  }))
);
const SupplierOrdersPage = lazy(() =>
  import("@/pages/reception/supplier-orders-page").then((m) => ({
    default: m.SupplierOrdersPage,
  }))
);
const SanitaryServicePage = lazy(() =>
  import("@/pages/reception/sanitary-service-page").then((m) => ({
    default: m.SanitaryServicePage,
  })),
);
// «Excedentes» passou a separador do Serviço Sanitário; o endereço antigo (marcadores, links) redireciona.
const RedirectToSanitaryExcess = lazy(() =>
  import("react-router-dom").then((m) => ({
    default: () => <m.Navigate to={ROUTES.RECEPTION.SANITARY_SERVICE_EXCESS_TAB} replace />,
  })),
);
const EmailTriagePage = lazy(() =>
  import("@/pages/email-triage/email-triage-page").then((m) => ({
    default: m.EmailTriagePage,
  }))
);

const CLIENT_READ = { access: "permission" as const, entity: "clients", action: "READ" };
const CONTACT_READ = { access: "permission" as const, entity: "contacts", action: "READ" };
const ORDER_READ = { access: "permission" as const, entity: "orders", action: "READ" };
const ORDER_CREATE = { access: "permission" as const, entity: "orders", action: "CREATE" };
// `factory_orders` é entidade do domínio `ordens_fabrico`, distinta de
// `orders` (`comercial`) — mesma separação já usada em `production.routes.tsx`.
// Ver o achado do auditor de permissões, 2026-09-06: as sub-páginas de OFs/
// expedição dentro da ficha do Cliente estavam gated só por `clients.READ`,
// e o backend responde 403 nas chamadas reais (`/orders/factory-orders/{id}`,
// `/orders/shipments/{id}`) a quem só tem acesso à ficha de Cliente.
const FACTORY_ORDER_READ = { access: "permission" as const, entity: "factory_orders", action: "READ" };
const ARTICLE_READ = { access: "permission" as const, entity: "articles", action: "READ" };
const OFFICE_STOCK_READ = { access: "permission" as const, entity: "office_supplies", action: "READ" };
const PHONE_CALLS_READ = { access: "permission" as const, entity: "phone_calls", action: "READ" };
const RECEIVED_MAIL_READ = { access: "permission" as const, entity: "received_mail", action: "READ" };
const PRINTERS_READ = { access: "permission" as const, entity: "printers", action: "READ" };
const SHIPMENTS_READ = { access: "permission" as const, entity: "reception_shipments", action: "READ" };
const INK_CARTRIDGES_READ = { access: "permission" as const, entity: "ink_cartridges", action: "READ" };
const RATE_QUOTES_READ = { access: "permission" as const, entity: "shipment_rate_quotes", action: "READ", feature: "rececao.api_parcelparcel" as const };
const SUPPLIER_ORDERS_READ = { access: "permission" as const, entity: "supplier_orders", action: "READ" };
const SANITARY_SERVICE_READ = { access: "permission" as const, entity: "sanitary_service", action: "READ" };
const EMAIL_TRIAGE_READ = { access: "permission" as const, entity: "email_triage", action: "READ" };

export const clientRoutes: RouteConfig[] = [
  // Client List & Detail
  { path: ROUTES.CLIENTS.LIST, component: ClientsPage, layout: "dashboard", permissions: CLIENT_READ },
  { path: ROUTES.CLIENTS.DETAIL, component: ClientDetailPage, layout: "dashboard", permissions: CLIENT_READ },

  // Contacts (Global) — LEVANTAMENTO-GAP.md 3.2
  { path: ROUTES.CONTACTS, component: ContactsPage, layout: "dashboard", permissions: CONTACT_READ },

  // Client Orders
  { path: ROUTES.CLIENTS.ORDERS.LIST, component: ClientOrdersPage, layout: "dashboard", permissions: ORDER_READ },
  { path: ROUTES.CLIENTS.ORDERS.DETAIL, component: OrderDetailPage, layout: "dashboard", permissions: ORDER_READ },
  { path: ROUTES.CLIENTS.ORDERS.CREATE, component: CreateOrderPage, layout: "dashboard", permissions: ORDER_CREATE },
  { path: ROUTES.CLIENTS.ORDERS.IMPORT, component: ImportPdfOrderPage, layout: "dashboard", permissions: ORDER_CREATE },

  // Client Invoices
  { path: ROUTES.CLIENTS.INVOICES, component: ClientInvoicesPage, layout: "dashboard", permissions: CLIENT_READ },

  // Client Articles
  { path: ROUTES.CLIENTS.ARTICLES.LIST, component: ClientArticlesPage, layout: "dashboard", permissions: ARTICLE_READ },
  { path: ROUTES.CLIENTS.ARTICLES.DETAIL, component: ArticleViewPage, layout: "dashboard", permissions: ARTICLE_READ },

  // Client Factory Orders
  { path: ROUTES.CLIENTS.FACTORY_ORDERS.LIST, component: ClientFactoryOrdersPage, layout: "dashboard", permissions: FACTORY_ORDER_READ },
  { path: ROUTES.CLIENTS.FACTORY_ORDERS.DETAIL, component: FactoryOrderDetailPage, layout: "dashboard", permissions: FACTORY_ORDER_READ },

  // Client Expedition
  { path: ROUTES.CLIENTS.EXPEDITION.LIST, component: ClientExpeditionPage, layout: "dashboard", permissions: ORDER_READ },
  { path: ROUTES.CLIENTS.EXPEDITION.DETAIL, component: ShipmentDetailPage, layout: "dashboard", permissions: ORDER_READ },

  // Receção (digitalização OCR e expediente)
  { path: ROUTES.RECEPTION.OFFICE_STOCK, component: OfficeStockPage, layout: "dashboard", permissions: OFFICE_STOCK_READ },
  { path: ROUTES.RECEPTION.CALLS, component: ReceptionCallsPage, layout: "dashboard", permissions: PHONE_CALLS_READ },
  { path: ROUTES.RECEPTION.MAIL, component: ReceptionMailPage, layout: "dashboard", permissions: RECEIVED_MAIL_READ },
  { path: ROUTES.RECEPTION.PRINTERS, component: ReceptionPrintersPage, layout: "dashboard", permissions: PRINTERS_READ },
  { path: ROUTES.RECEPTION.SHIPMENTS, component: ReceptionShipmentsPage, layout: "dashboard", permissions: SHIPMENTS_READ },
  { path: ROUTES.RECEPTION.INK_CARTRIDGES, component: ReceptionInkCartridgesPage, layout: "dashboard", permissions: INK_CARTRIDGES_READ },
  { path: ROUTES.RECEPTION.SHIPMENT_SIMULATION, component: ShipmentSimulationPage, layout: "dashboard", permissions: RATE_QUOTES_READ },
  // Pedir material: qualquer colaborador (a feature `pedidos_material` e o módulo fecham-no no servidor).
  { path: ROUTES.RECEPTION.OFFICE_REQUESTS, component: OfficeSupplyRequestsPage, layout: "dashboard", permissions: { access: "authenticated", feature: "rececao.pedidos_material" as const } },
  { path: ROUTES.RECEPTION.SUPPLIER_ORDERS, component: SupplierOrdersPage, layout: "dashboard", permissions: SUPPLIER_ORDERS_READ },

  // Serviço Sanitário — contrato de higiene/têxteis (entregas, faturas, excedentes)
  { path: ROUTES.RECEPTION.SANITARY_SERVICE, component: SanitaryServicePage, layout: "dashboard", permissions: SANITARY_SERVICE_READ },
  { path: ROUTES.RECEPTION.SANITARY_EXCESS, component: RedirectToSanitaryExcess, layout: "dashboard", permissions: SANITARY_SERVICE_READ },

  // Triagem de email
  { path: ROUTES.EMAIL_TRIAGE, component: EmailTriagePage, layout: "dashboard", permissions: EMAIL_TRIAGE_READ },
];
